import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getCampaign } from '../api/client'
import { Helmet } from 'react-helmet-async'
import {
  FileText, Thermometer, ArrowRight,
  Check, Download, ClipboardList, Leaf, MapPin, Calculator, ChefHat,
  QrCode, Camera, Clock, Smartphone, Zap, Printer, Scissors, Tag,
} from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../components/MarketingLayout'
import { useInstallPrompt } from '../components/InstallAppButton'

const FEATURES = [
  {
    icon: Calculator,
    title: 'Receptből kalória számítás',
    desc: '400+ alapanyag beépítve. Beírod a mennyiségeket — kalória, fehérje, zsír, szénhidrát kiszámolva 100g-ra és adagonként is.',
    href: '/receptbol-kaloria-szamitas',
  },
  {
    icon: FileText,
    title: 'Gyártmánylap & etikett-címke',
    desc: 'Kötelező allergénjelölés, lejárat, tárolási feltételek — automatikusan kitöltve, nyomtatásra készen. 3 etikett méret (100×70, 50×30, A4).',
    href: '/gyartmanylap',
  },
  {
    icon: Thermometer,
    title: 'HACCP igazolás & napló',
    desc: 'Felejtsd el a kockás füzetet. Hűtő hőmérsékletek, takarítási és termelési napló — vezethető telefonról is, hatósági ellenőrzésre kész.',
    href: '/haccp-igazolas',
  },
  {
    icon: MapPin,
    title: 'Származási igazolás',
    desc: 'Óvodába, iskolába, rendezvényre szállítasz? Egy kattintásra kész a származási igazolás, automatikus dátumkitöltéssel.',
    href: '/szarmazasi-igazolas',
  },
]

const STEPS = [
  {
    num: '01',
    title: 'Beírod a receptet',
    desc: 'Add meg a receptben használt alapanyagokat és mennyiségeket. Semmi bonyolult.',
  },
  {
    num: '02',
    title: 'Mi kiszámolunk mindent',
    desc: 'Tápérték, allergének, kötelező jelölések — azonnal, automatikusan. Nem kell hozzá semmilyen szaktudás.',
  },
  {
    num: '03',
    title: 'Kinyomtatod és kész',
    desc: 'Gyártmánylap, etikett-címke (3 méretben), HACCP kísérőirat, származási igazolás — töltsd le és nyomtasd ki.',
  },
]

const PLANS = [
  {
    name: 'Ingyenes',
    price: '0 Ft',
    sub: '',
    features: [
      '3 recept',
      'Tápérték és allergén lista',
      'Gyártmánylap PDF',
      'Származási igazolás PDF',
      'Etikett-címke (3 méretben)',
    ],
    cta: 'Kipróbálom ingyen',
    highlight: false,
    stripeMonthly: null,
    stripeAnnual: null,
  },
  {
    name: 'Alap',
    price: '2 990 Ft',
    sub: '/hó + ÁFA',
    brutto: '(3 796 Ft/hó bruttó)',
    annualPrice: '29 900 Ft/év',
    annualFull: '35 880 Ft/év',
    annualBrutto: '(37 973 Ft/év bruttó)',
    badge: 'Legtöbbek választása',
    features: [
      '20 recept',
      'Minden ingyenes funkció',
      'Digitális HACCP naplók',
      'Munkatárs-kezelés',
      'Félkész termék receptek',
      'E-mail support munkaidőben',
    ],
    cta: 'Havi előfizetés',
    highlight: true,
    stripeMonthly: 'starter_monthly',
    stripeAnnual: 'starter_annual',
  },
  {
    name: 'Pro',
    price: '5 990 Ft',
    sub: '/hó + ÁFA',
    brutto: '(7 607 Ft/hó bruttó)',
    annualPrice: '59 900 Ft/év',
    annualFull: '71 880 Ft/év',
    annualBrutto: '(76 073 Ft/év bruttó)',
    features: [
      'Korlátlan recept',
      'Minden Alap funkció',
      'Saját logó a PDF-eken',
      'Telefonos support',
    ],
    cta: 'Havi előfizetés',
    highlight: false,
    stripeMonthly: 'pro_monthly',
    stripeAnnual: 'pro_annual',
  },
]

const FAQS = [
  {
    q: 'Kell hozzá valamilyen különleges szoftver vagy gép?',
    a: 'Nem. Bármilyen böngészőből működik — számítógépen, tableten, akár telefonon is. Semmi telepíteni való.',
  },
  {
    q: 'Mennyi mindent kell kézzel bevinni?',
    a: 'Csak az alapanyag nevét és a mennyiséget. A tápértékeket, allergéneket és kötelező jelöléseket mi számoljuk ki — 400+ alapanyag adatbázisa már be van töltve.',
  },
  {
    q: 'Elfogadja a hatóság az így készült dokumentumokat?',
    a: 'Igen. A gyártmánylap és a HACCP dokumentáció megfelel a magyar jogszabályoknak és az EU 1169/2011 rendeletének.',
  },
  {
    q: 'Mi van, ha elfogy a recept-keretem?',
    a: 'Ha elégedett vagy a szolgáltatásunkkal, bármikor válthatsz nagyobb csomagra, az adataid megmaradnak.',
  },
  {
    q: 'Mi történik, ha hónap közben csomagot váltok?',
    a: 'Nagyobb csomagra vagy éves fizetésre váltáskor a váltás azonnal életbe lép: a régi csomag fel nem használt napjait jóváírjuk, és csak a különbözetet kell kifizetned. Kisebb csomagra vagy havi fizetésre váltáskor a már kifizetett időszak végéig megmarad a jelenlegi csomagod, utána automatikusan átállunk. Dupla díjat egyik esetben sem fizetsz.',
  },
  {
    q: 'Bármikor lemondható? Automatikusan megújul?',
    a: 'Igen, bármikor lemondható, egy kattintással az Előfizetés oldalon. Nincs kötöttség, nincs felmondási idő, nincs rejtett díj. Fontos: amíg nem mondod le, az előfizetés automatikusan megújul — a havi havonta, az éves évente —, és a díjat a megadott bankkártyáról levonjuk. Lemondás után a már kifizetett időszak végéig minden elérhető marad.',
  },
  {
    q: 'Mi lesz a receptjeimmel, ha lemondom az előfizetést?',
    a: 'A fiókod és minden felvitt recepted megmarad, de a kifizetett időszak végén ingyenes csomagra váltasz: ekkor csak az első 3 recepted használható, a többi inaktív lesz (nem szerkeszthető, nem tölthető le hozzá gyártmánylap vagy címke), és a HACCP-naplók sem érhetők el. Ha újra előfizetsz, minden recept azonnal újra elérhető.',
  },
]

const FAQ_JSONLD = faqJsonLd(FAQS)

const SOFTWARE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CukrászDoc',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: 'Gyártmánylap, HACCP igazolás, származási igazolás és receptből kalória számítás automatikusan, percek alatt.',
  url: 'https://cukraszdoc.hu/',
  offers: [
    { '@type': 'Offer', name: 'Ingyenes', price: '0', priceCurrency: 'HUF' },
    { '@type': 'Offer', name: 'Alap', price: '2990', priceCurrency: 'HUF' },
    { '@type': 'Offer', name: 'Pro', price: '5990', priceCurrency: 'HUF' },
  ],
}

export default function LandingPage() {
  const { canInstall, promptInstall } = useInstallPrompt()
  const [campaignActive, setCampaignActive] = useState(false)
  useEffect(() => {
    getCampaign().then(r => setCampaignActive(!!r.data.active)).catch(() => {})
  }, [])
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Gyártmánylap készítés online, gyorsan — CukrászDoc</title>
        <meta name="description" content="Gyártmánylap, HACCP igazolás, származási igazolás és receptből kalória számítás automatikusan, percek alatt. Magyar cukrászdáknak és vendéglátóhelyeknek." />
        <link rel="canonical" href="https://cukraszdoc.hu/" />
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>
      <MarketingNavBar />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }}
        className="relative overflow-hidden"
      >
        {/* subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 py-14 lg:py-20">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
              <Leaf size={12} />
              Magyar cukrászdáknak &amp; vendéglátóhelyeknek
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold leading-tight text-white mb-4">
              Gyártmánylap, gyorsan —<br />
              <span style={{ color: TEAL }}>receptből kész</span><br />
              dokumentáció.
            </h1>

            <p className="text-lg sm:text-xl mb-6 max-w-xl leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.6)' }}>
              Beírod a receptet — mi elvégezzük a papírmunkát. Gyártmánylap, HACCP igazolás, származási igazolás és receptből kalória számítás percek alatt, nyomtatásra készen.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Link to="/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all"
                style={{ background: TEAL, color: '#fff' }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}>
                Próbáld ki ingyen <ArrowRight size={16} />
              </Link>
              <Link to="/login"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.14)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}>
                Bejelentkezés
              </Link>
              {canInstall && (
                <button onClick={promptInstall}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all"
                  style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.4)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(91,184,160,0.25)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'rgba(91,184,160,0.15)')}>
                  <Download size={18} />
                  Alkalmazás telepítése
                </button>
              )}
            </div>

          </div>

          {/* Trust bar */}
          <div className="flex flex-wrap sm:flex-nowrap justify-between w-full gap-5 mt-6">
            {[
              { icon: Calculator, label: '400+ alapanyag beépítve' },
              { icon: Download, label: '4 dokumentumtípus' },
              { icon: ClipboardList, label: 'Hatósági ellenőrzésre kész' },
              { icon: FileText, label: 'Ingyenesen kipróbálható' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <Icon size={22} style={{ color: TEAL }} />
                <span className="text-base sm:text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Wave divider */}
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* ── PAIN POINTS ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: DARK }}>
              Ismerős valamelyik?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { q: 'Papíron vagy Excelben vezeted a HACCP naplót?', a: 'Digitálisan is megcsinálhatod — telefonról, bárhonnan.' },
              { q: 'Nincs kapacitásod minden termékhez külön gyártmánylapot készíteni?', a: 'Csak írd be a receptet — egy kattintásra elkészítjük neked.' },
              { q: 'Fél szemmel mindig az ellenőrtől tartasz?', a: 'Minden dokumentum percek alatt kinyomtatható, hatósági ellenőrzésre készen.' },
            ].map(({ q, a }) => (
              <div key={q} className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
                <p className="font-semibold text-sm mb-3" style={{ color: DARK }}>{q}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#6b7280' }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: DARK }}>
              Minden, ami egy cukrászdának kell
            </h2>
            <p className="text-base text-gray-500 max-w-xl mx-auto">
              Nem kell szoftver-tudás, vagy külön munkaerő a dokumentációk elkészítéséhez. Csak írd be a receptjedet, mi elvégezzük a papírmunkát.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc, href }) => (
              <Link key={title} to={href}
                className="bg-white rounded-2xl p-6 transition-all duration-200 hover:-translate-y-1 flex flex-col"
                style={{ border: '1px solid rgba(91,184,160,0.15)', boxShadow: '0 2px 12px rgba(30,45,39,0.06)' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(91,184,160,0.12)' }}>
                  <Icon size={22} style={{ color: TEAL }} />
                </div>
                <h3 className="font-semibold mb-2 text-base min-h-[3rem]" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: DARK }}>
              Hogyan működik?
            </h2>
            <p className="text-base text-gray-500">Három lépés és kész a papírmunka. Szoftver-tudás nélkül.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* connector line */}
            <div className="hidden md:block absolute top-10 left-[calc(16.66%+2rem)] right-[calc(16.66%+2rem)] h-px"
              style={{ background: `linear-gradient(90deg, ${TEAL}, rgba(91,184,160,0.3))` }} />

            {STEPS.map(({ num, title, desc }) => (
              <div key={num} className="relative text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 mx-auto"
                  style={{ background: DARK }}>
                  <span className="font-bold text-xl" style={{ color: TEAL }}>{num}</span>
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APP LETÖLTÉSE ────────────────────────────────────────────────── */}
      <section style={{ background: BG }} className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <Smartphone size={12} />
            Telepíthető alkalmazás
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: DARK }}>
            Tedd fel a kezdőképernyődre — böngésző nélkül is
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto mb-8 leading-relaxed">
            A CukrászDoc telepíthető, mint egy hagyományos alkalmazás — telefonon vagy gépen egyaránt.
          </p>

          <div className="max-w-lg mx-auto mb-10 flex items-start gap-4 text-left bg-white rounded-2xl p-6"
            style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(91,184,160,0.12)' }}>
              <Zap size={20} style={{ color: TEAL }} />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: DARK }}>
              <strong>Gyorsabb elérés a pult mögött.</strong> Amikor a hűtő hőmérsékletét kell rögzítened, vagy gyorsan meg kell nézned egy receptet, nem böngészőt nyitsz és linket keresel — rákoppintasz az ikonra a kezdőképernyőn, és már bent is vagy.
            </p>
          </div>

          {canInstall ? (
            <button onClick={promptInstall}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all"
              style={{ background: TEAL, color: '#fff' }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}>
              <Download size={18} /> Alkalmazás telepítése
            </button>
          ) : (
            <p className="text-sm text-gray-500">
              iPhone-on a Safari <strong>Megosztás</strong> menüjében a <strong>„Kezdőképernyőhöz adás”</strong>, Androidon vagy Chrome-ban a böngésző menüjében az <strong>„Alkalmazás telepítése”</strong> pont alatt találod.
            </p>
          )}
        </div>
      </section>

      {/* ── NYOMONKÖVETÉS QR-KÓDDAL ──────────────────────────────────────── */}
      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)` }} className="py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
                <QrCode size={12} />
                Újdonság
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                Alapanyag nyomonkövetés <span style={{ color: TEAL }}>QR-kóddal</span> — drága kütyük nélkül
              </h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Rögzítsd, melyik beszállítótól érkezett egy alapanyag-tétel, és tedd rá a QR-kódot a címkére — beolvasva megmutatja, miből, mikor készült a termék, és mikor jár le. Bármelyik nyomtatóval, bármelyik telefonnal működik, nem kell hozzá se speciális nyomtató, se szkenner.
              </p>
              <div className="flex flex-col gap-3 mb-8">
                {[
                  { icon: QrCode, text: 'QR-kód a címkén, egy kattintással generálva' },
                  { icon: Clock, text: 'Gyártás dátuma és lejárat egy pillantásra, beolvasva' },
                  { icon: Camera, text: 'Beszállítói számla fotóként tárolható (bővítmény)' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(91,184,160,0.15)' }}>
                      <Icon size={16} style={{ color: TEAL }} />
                    </div>
                    <span className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
                  Próbáld ki ingyen <ArrowRight size={16} />
                </Link>
                <Link to="/blog/alapanyag-nyomonkoveres-qr-koddal" className="text-sm font-semibold hover:underline" style={{ color: TEAL }}>
                  Hogyan működik? →
                </Link>
              </div>
            </div>
            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(91,184,160,0.2)' }}>
              <div className="rounded-xl overflow-hidden bg-white">
                <div className="p-4" style={{ background: '#4E813F' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-bold text-sm">Meggyes Csók</span>
                    <div className="w-10 h-10 bg-white rounded p-1 flex items-center justify-center">
                      <QrCode size={28} style={{ color: DARK }} />
                    </div>
                  </div>
                </div>
                <div className="p-4 flex gap-3">
                  <div className="flex-1 rounded-lg p-2.5" style={{ background: BG }}>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400">Gyártva</p>
                    <p className="text-xs font-semibold" style={{ color: DARK }}>2026.07.31</p>
                  </div>
                  <div className="flex-1 rounded-lg p-2.5" style={{ background: 'rgba(91,184,160,0.12)' }}>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: TEAL }}>Fogyasztható</p>
                    <p className="text-xs font-semibold" style={{ color: DARK }}>2026.08.03</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-center mt-4" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Így néz ki beolvasva egy nyomonkövetett termék adatlapja
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CÍMKENYOMTATÓ (Brother QL) ───────────────────────────────────── */}
      <section style={{ background: BG }} className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(91,184,160,0.15)', color: DARK, border: '1px solid rgba(91,184,160,0.35)' }}>
                <Printer size={12} />
                Címkenyomtató
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight" style={{ color: DARK }}>
                Konyhai címke és etikett <span style={{ color: TEAL }}>egy kattintással</span>
              </h2>
              <p className="text-base leading-relaxed mb-6 text-gray-600">
                Brother QL címkenyomtatóval (QL-800, QL-810W, QL-820NWB, 62 mm-es tekercs) a címke nyomtatási ablak nélkül,
                azonnal kijön — és pontosan olyan hosszú, amilyen a tartalma. Ha már van ilyen nyomtatód, például egy másik
                HACCP-programból, az nálunk is működik: <b>nem kell hozzá drága hardvercsomag</b>.
              </p>
              <div className="flex flex-col gap-3 mb-8">
                {[
                  { icon: Tag, text: 'Konyhai címke: lejárat, ki készítette, tételszám, allergének, QR-kód' },
                  { icon: FileText, text: 'Vásárlói etikett összetevőkkel és tápértékkel, kicsi vagy nagy méretben' },
                  { icon: Scissors, text: 'Ott vág, ahol a címke véget ér — nincs papírpazarlás' },
                  { icon: Clock, text: 'Termelési naplóból, félkész készletből és pultnaplóból is, egy kattintással' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(91,184,160,0.18)' }}>
                      <Icon size={16} style={{ color: DARK }} />
                    </div>
                    <span className="text-sm text-gray-700">{text}</span>
                  </div>
                ))}
              </div>
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
                Próbáld ki ingyen <ArrowRight size={16} />
              </Link>
            </div>

            {/* konyhai címke minta (62 mm-es tekercs, arányosan) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-full max-w-sm bg-white rounded-md shadow-lg px-5 py-4 font-sans text-black" style={{ border: '1px solid #e5e7eb' }}>
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-bold leading-tight">Vaníliakrém</div>
                    <div className="text-base font-bold mt-0.5">LEJÁR: 2026.11.03. 08:30</div>
                    <div className="text-xs mt-1">Gyártva: 2026.11.01. 08:30 · Hűtve 2–8 °C</div>
                    <div className="text-xs">Készítette: Kovács Anna · Tétel: B-0412</div>
                  </div>
                  <QrCode size={64} strokeWidth={1.5} className="shrink-0" />
                </div>
                <div className="text-xs font-bold mt-2 pt-1.5" style={{ borderTop: '1.5px solid #000' }}>Allergén: TEJ, TOJÁS</div>
              </div>
              <p className="text-xs text-gray-500">Konyhai címke — 62 mm széles, kb. 2 cm hosszú</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── MIÉRT A CUKRÁSZDOC ──────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-2xl p-8 sm:p-10" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: TEAL, letterSpacing: '0.08em' }}>
              Miért a CukrászDoc?
            </p>
            <p className="text-lg leading-relaxed mb-4" style={{ color: DARK }}>
              A 2026-os Sirhán közel 30 standot jártam végig — mind HACCP- és élelmiszerbiztonsági szoftvert kínált. Méregdrága éves licenc, kötelező hardvercsomag hozzá, és a végén egy felület, amit egy cukrász a saját műhelyében, élesben valójában nem bírt volna használni.
            </p>
            <p className="text-lg leading-relaxed mb-4" style={{ color: DARK }}>
              Mi magunk is cukrászdát és rendezvényszolgáltatást üzemeltetünk Budapesten, úgyhogy pontosan tudtuk, mire van szükség — nem egy könyvelői Excel-klónra, hanem valamire, amibe beírod a receptet, és onnantól minden más automatikusan elkészül. Ezért épült meg a CukrászDoc: nem szoftvercégként, hanem cukrászdatulajdonosként, akinek elege lett abból, amit a piacon talált.
            </p>
            <p className="text-sm font-semibold" style={{ color: TEAL }}>— Papp Virág Luca, alapító</p>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: DARK }}>
              Átlátható árazás
            </h2>
            <p className="text-base text-gray-500">
              Kártyaszám nélkül kipróbálható. Ha nem tetszik, egyszerűen abbahagyod.
            </p>
            {campaignActive && (
              <div className="mt-5 inline-block px-4 py-2 rounded-full text-sm font-semibold"
                style={{ background: 'rgba(91,184,160,0.15)', color: DARK }}>
                🎄 Karácsonyi akció: minden éves előfizetés −20% december 24-ig
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {PLANS.map(plan => (
              <div key={plan.name}
                className="rounded-2xl p-7 relative transition-all duration-200"
                style={plan.highlight ? {
                  background: DARK,
                  border: `2px solid ${TEAL}`,
                  boxShadow: `0 8px 40px rgba(91,184,160,0.25)`,
                } : {
                  background: '#fff',
                  border: '1px solid rgba(30,45,39,0.1)',
                  boxShadow: '0 2px 12px rgba(30,45,39,0.06)',
                }}>

                {plan.highlight && 'badge' in plan && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                    style={{ background: TEAL, color: '#fff' }}>
                    {(plan as any).badge}
                  </div>
                )}

                <div className="mb-6">
                  <p className="font-semibold text-sm mb-1"
                    style={{ color: plan.highlight ? TEAL : TEAL }}>
                    {plan.name}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold" style={{ color: plan.highlight ? '#fff' : DARK }}>
                      {plan.price}
                    </span>
                    <span className="text-sm" style={{ color: plan.highlight ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                      {plan.sub}
                    </span>
                  </div>
                  {'brutto' in plan && (
                    <p className="text-xs mt-0.5" style={{ color: plan.highlight ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                      {(plan as any).brutto}
                    </p>
                  )}
                  {'annualPrice' in plan && (
                    <div className="mt-2">
                      <span className="text-sm font-semibold" style={{ color: TEAL }}>
                        {(plan as any).annualPrice} · 2 hónap ingyen
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: plan.highlight ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}>
                        {(plan as any).annualBrutto}
                      </p>
                    </div>
                  )}
                </div>

                <ul className="space-y-2.5 mb-7">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: plan.highlight ? 'rgba(91,184,160,0.2)' : 'rgba(91,184,160,0.12)' }}>
                        <Check size={10} style={{ color: TEAL }} />
                      </div>
                      <span style={{ color: plan.highlight ? 'rgba(255,255,255,0.8)' : '#374151' }}>{f}</span>
                    </li>
                  ))}
                </ul>

                {plan.stripeMonthly ? (
                  <div className="space-y-2">
                    <Link to={`/register?plan=${plan.stripeMonthly}`}
                      className="block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all"
                      style={plan.highlight ? { background: TEAL, color: '#fff' } : { background: 'rgba(30,45,39,0.07)', color: DARK }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                      {plan.cta}
                    </Link>
                    <Link to={`/register?plan=${plan.stripeAnnual}`}
                      className="block w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all"
                      style={plan.highlight
                        ? { background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }
                        : { background: 'transparent', color: TEAL, border: `1px solid ${TEAL}` }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                      Éves — 2 hónap ingyen
                    </Link>
                    <p className="text-xs text-center pt-1" style={{ color: plan.highlight ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>
                      Automatikusan megújul, amíg le nem mondod · bármikor lemondható
                    </p>
                  </div>
                ) : (
                  <Link to="/register"
                    className="block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all"
                    style={{ background: 'rgba(30,45,39,0.07)', color: DARK }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center items-center gap-6 mt-10">
            {[
              { icon: '🔒', label: 'Biztonságos fizetés' },
              { icon: '✓', label: 'Bármikor lemondható' },
              { icon: '✓', label: 'Ingyenes regisztráció' },
              { icon: '✓', label: 'Éves előfizetés esetén 2 hónap ajándék' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-sm" style={{ color: TEAL }}>{icon}</span>
                <span className="text-sm text-gray-500">{label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6 max-w-2xl mx-auto">
            <strong className="text-gray-700">Csomagváltás bármikor, igazságosan:</strong> nagyobb csomagra váltáskor
            a régi csomag fel nem használt napjait jóváírjuk, csak a különbözetet fizeted. Kisebb csomagra váltáskor
            a kifizetett időszak végéig megmarad a jelenlegi csomagod.
          </p>
          <p className="text-center text-sm text-gray-500 mt-3 max-w-2xl mx-auto">
            <strong className="text-gray-700">Automatikus megújulás:</strong> a havi előfizetés havonta, az éves évente
            automatikusan megújul, és a díjat a bankkártyádról levonjuk, amíg le nem mondod. Lemondani bármikor lehet
            az Előfizetés oldalon, egy kattintással — a kifizetett időszak végéig minden elérhető marad.
          </p>
          <p className="text-center text-xs text-gray-400 mt-4">Az árak nettó árak, 27% ÁFÁ-t nem tartalmaznak.</p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: DARK }}>
              Gyakori kérdések
            </h2>
            <p className="text-base text-gray-500">Ha valami nem egyértelmű, valószínűleg itt megtalálod a választ.</p>
          </div>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/" />

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section style={{ background: DARK }} className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(${TEAL} 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(91,184,160,0.15)', border: '1px solid rgba(91,184,160,0.25)' }}>
            <ChefHat size={26} style={{ color: TEAL }} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Kezdd el ma — ingyen.
          </h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Regisztrálj, írd be az első receptedet, és percek alatt kinyomtatható a gyártmánylap.
            Nem kell hozzá semmilyen szoftver-tudás.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all"
              style={{ background: TEAL, color: '#fff' }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}>
              Ingyenes regisztráció <ArrowRight size={17} />
            </Link>
            {canInstall && (
              <button onClick={promptInstall}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all"
                style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.4)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(91,184,160,0.25)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(91,184,160,0.15)')}>
                <Download size={18} />
                Alkalmazás telepítése
              </button>
            )}
          </div>
          <p className="mt-4 text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Már van fiókod?{' '}
            <Link to="/login" style={{ color: TEAL }} className="hover:underline">Jelentkezz be</Link>
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
